import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { SUBSCRIPTION_TIERS } from '../utils/stripe';
import {
  TrendingUp,
  TrendingDown,
  Brain,
  BarChart3,
  History,
  MessageSquare,
  Search,
  Check,
  ChevronDown,
  ArrowRight,
  Zap,
  Shield,
  Globe,
  Sparkles,
  LineChart,
  Target,
  Activity,
} from 'lucide-react';

// Rotating keywords for hero
const ROTATING_WORDS = ['Sentiment', 'Disconnect', 'Opportunity', 'Analysis'];

// Get app URL based on environment
const getAppUrl = (path: string = '') => {
  const hostname = window.location.hostname;
  // In production, use the app subdomain
  if (hostname === 'stocksentiment.io' || hostname === 'www.stocksentiment.io') {
    return `https://app.stocksentiment.io${path}`;
  }
  // On app subdomain or localhost, use relative paths
  if (hostname.startsWith('app.') || hostname === 'localhost') {
    return path || '/';
  }
  // Default fallback
  return `/app${path}`;
};

// Grid background component
function GridBackground() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
        }}
      />
      {/* Radial gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#060606]" />
      {/* Glow effects */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-emerald-500/5 rounded-full blur-[120px]" />
      <div className="absolute top-1/3 right-0 w-[400px] h-[400px] bg-teal-500/5 rounded-full blur-[100px]" />
    </div>
  );
}

// Animated rotating text
function RotatingText() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % ROTATING_WORDS.length);
        setIsAnimating(false);
      }, 200);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  return (
    <span className="relative inline-block min-w-[180px] sm:min-w-[280px]">
      <span
        className={clsx(
          'text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-teal-400 to-emerald-400 transition-all duration-200',
          isAnimating ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'
        )}
      >
        {ROTATING_WORDS[currentIndex]}
      </span>
    </span>
  );
}

// Feature badge
function FeatureBadge({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={clsx(
      'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium',
      'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20',
      className
    )}>
      {children}
    </span>
  );
}

// Social icon button
function SocialIcon({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="w-9 h-9 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-gray-400 hover:text-white transition-all"
    >
      {children}
    </a>
  );
}

// Floating card component
function FloatingCard({
  children,
  className = '',
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  return (
    <div
      className={clsx(
        'relative bg-[#0c0c0c] border border-white/10 rounded-2xl shadow-2xl shadow-black/50',
        'animate-float',
        className
      )}
      style={{ animationDelay: `${delay}ms` }}
    >
      {children}
      {/* Glow effect */}
      <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-b from-emerald-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity -z-10 blur-xl" />
    </div>
  );
}

// Demo preview card
function DemoPreviewCard() {
  return (
    <FloatingCard className="w-full max-w-[500px] overflow-hidden">
      <div className="p-4 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-500/60" />
          <div className="w-3 h-3 rounded-full bg-yellow-500/60" />
          <div className="w-3 h-3 rounded-full bg-emerald-500/60" />
        </div>
        <span className="text-xs text-gray-500">SentimentEdge Dashboard</span>
      </div>
      <div className="p-6 space-y-4">
        {/* Stock card */}
        <div className="bg-white/5 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500/20 to-teal-500/20 flex items-center justify-center">
                <span className="text-sm font-bold text-emerald-400">TSLA</span>
              </div>
              <div>
                <p className="font-semibold text-white">Tesla Inc.</p>
                <p className="text-xs text-gray-500">Automotive</p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-bold text-white">$248.50</p>
              <p className="text-xs text-emerald-400">+2.4%</p>
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-gray-500">Sentiment:</span>
              <span className="text-red-400 font-medium">-32</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-500">Disconnect:</span>
              <span className="text-emerald-400 font-medium">+67</span>
            </div>
          </div>
          <div className="mt-3 h-1 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full w-[67%] rounded-full bg-gradient-to-r from-emerald-500 to-teal-500" />
          </div>
        </div>
        {/* Signal indicator */}
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-sm font-semibold text-emerald-400">Strong Buy Signal</p>
              <p className="text-xs text-gray-400">Negative sentiment, strong fundamentals</p>
            </div>
          </div>
        </div>
      </div>
    </FloatingCard>
  );
}

// Feature showcase card
function FeatureShowcase({
  icon: Icon,
  title,
  description,
  badge,
}: {
  icon: typeof Brain;
  title: string;
  description: string;
  badge?: string;
}) {
  return (
    <div className="group relative p-6 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-emerald-500/30 transition-all duration-500">
      {/* Hover glow */}
      <div className="absolute inset-0 rounded-2xl bg-gradient-to-b from-emerald-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="relative">
        {badge && (
          <FeatureBadge className="absolute -top-2 -right-2">
            {badge}
          </FeatureBadge>
        )}
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-teal-500/10 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-500">
          <Icon className="w-7 h-7 text-emerald-400" />
        </div>
        <h3 className="text-xl font-semibold text-white mb-3">{title}</h3>
        <p className="text-gray-400 leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

// Pricing section with billing toggle
function PricingSection() {
  const [isYearly, setIsYearly] = useState(false);

  return (
    <section id="pricing" className="relative py-32 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <FeatureBadge className="mb-6">
            <Shield className="w-3 h-3" />
            Pricing
          </FeatureBadge>
          <h2 className="text-4xl md:text-5xl font-serif mb-6">
            Simple, Transparent Pricing
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Start free. Upgrade when you need more.
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-3 bg-white/5 rounded-full p-1.5">
            <button
              onClick={() => setIsYearly(false)}
              className={clsx(
                'px-6 py-2 rounded-full text-sm font-medium transition-all',
                !isYearly
                  ? 'bg-emerald-500 text-black'
                  : 'text-gray-400 hover:text-white'
              )}
            >
              Monthly
            </button>
            <button
              onClick={() => setIsYearly(true)}
              className={clsx(
                'px-6 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2',
                isYearly
                  ? 'bg-emerald-500 text-black'
                  : 'text-gray-400 hover:text-white'
              )}
            >
              Yearly
              <span className={clsx(
                'text-xs px-2 py-0.5 rounded-full',
                isYearly ? 'bg-black/20 text-black' : 'bg-emerald-500/20 text-emerald-400'
              )}>
                Save 32%
              </span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          <PricingCard
            name="Free"
            monthlyPrice="$0"
            period="/forever"
            description="Perfect for getting started"
            features={[
              '5 stocks tracked',
              'Basic sentiment analysis',
              '7 days of history',
              'Daily updates',
            ]}
            tier="free"
            ctaText="Start Free"
            isYearly={isYearly}
          />
          <PricingCard
            name="Early Supporter"
            monthlyPrice="$199"
            period=" one-time"
            description="Support us & get lifetime access"
            features={[
              'Lifetime access',
              '100 tracked stocks',
              '1 year of history',
              'AI Chat assistant',
              'Backtesting simulator',
              'All future Pro features',
            ]}
            tier="early_supporter"
            monthlyPriceId={SUBSCRIPTION_TIERS.early_supporter.priceId}
            popular
            badge="Limited Time"
            ctaText="Get Lifetime Access"
            isYearly={isYearly}
          />
          <PricingCard
            name="Starter"
            monthlyPrice="$49"
            yearlyPrice="$399"
            period="/month"
            description="For casual investors"
            features={[
              '25 tracked stocks',
              '30 days of history',
              'AI Chat assistant',
              'AI sentiment summaries',
              'Email alerts',
            ]}
            tier="starter"
            monthlyPriceId={SUBSCRIPTION_TIERS.starter.priceIds.monthly}
            yearlyPriceId={SUBSCRIPTION_TIERS.starter.priceIds.yearly}
            ctaText="Get Starter"
            isYearly={isYearly}
          />
          <PricingCard
            name="Pro"
            monthlyPrice="$99"
            yearlyPrice="$799"
            period="/month"
            description="For serious researchers"
            features={[
              '100 tracked stocks',
              '1 year of history',
              'AI Chat assistant',
              'Backtesting simulator',
              'Performance analytics',
              'Data export',
            ]}
            tier="pro"
            monthlyPriceId={SUBSCRIPTION_TIERS.pro.priceIds.monthly}
            yearlyPriceId={SUBSCRIPTION_TIERS.pro.priceIds.yearly}
            ctaText="Upgrade to Pro"
            isYearly={isYearly}
          />
          <PricingCard
            name="Enterprise"
            monthlyPrice="Custom"
            period=""
            description="For institutions"
            features={[
              'Unlimited stocks',
              'Full historical data',
              'AI Chat assistant',
              'Full API access',
              'Dedicated account manager',
              'SLA guarantee',
            ]}
            tier="enterprise"
            ctaText="Contact Us"
            isYearly={isYearly}
          />
        </div>

        <p className="text-center text-gray-500 mt-12 text-sm">
          Start free, upgrade anytime. Cancel your subscription whenever you want.
        </p>
      </div>
    </section>
  );
}

// Pricing card
function PricingCard({
  name,
  monthlyPrice,
  yearlyPrice,
  period,
  description,
  features,
  popular = false,
  badge,
  ctaText = 'Get Started',
  monthlyPriceId,
  yearlyPriceId,
  tier,
  isYearly,
}: {
  name: string;
  monthlyPrice: string;
  yearlyPrice?: string;
  period: string;
  description: string;
  features: string[];
  popular?: boolean;
  badge?: string;
  ctaText?: string;
  monthlyPriceId?: string;
  yearlyPriceId?: string;
  tier: 'free' | 'early_supporter' | 'starter' | 'pro' | 'enterprise';
  isYearly: boolean;
}) {
  // Calculate savings for yearly billing
  const monthlyNum = parseInt(monthlyPrice.replace('$', '')) || 0;
  const yearlyNum = parseInt(yearlyPrice?.replace('$', '') || '0') || 0;
  const yearlySavings = yearlyPrice ? (monthlyNum * 12) - yearlyNum : 0;
  const displayPrice = isYearly && yearlyPrice ? yearlyPrice : monthlyPrice;
  const displayPeriod = isYearly && yearlyPrice ? '/year' : period;
  const priceId = isYearly && yearlyPriceId ? yearlyPriceId : monthlyPriceId;
  const navigate = useNavigate();

  const handleClick = () => {
    // Free tier - go to login (will redirect to dashboard if already authenticated)
    if (tier === 'free') {
      navigate('/login');
      return;
    }

    // Enterprise - contact sales
    if (tier === 'enterprise') {
      window.location.href = 'mailto:sales@stocksentiment.io?subject=Enterprise%20Plan%20Inquiry';
      return;
    }

    // Paid tiers - go to login page first (handles both auth states)
    // The login page will redirect to Stripe checkout after authentication
    navigate('/login', {
      state: {
        returnTo: 'subscribe',
        tier: tier,
        priceId: priceId,
        message: `Sign in to subscribe to the ${name} plan`
      }
    });
  };

  return (
    <div
      className={clsx(
        'relative p-8 rounded-3xl transition-all duration-500 group flex flex-col',
        popular
          ? 'bg-gradient-to-b from-emerald-500/10 to-transparent border-2 border-emerald-500/50'
          : 'bg-white/[0.02] border border-white/10 hover:border-white/20'
      )}
    >
      {badge && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <span className="px-4 py-1.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-sm font-bold text-white whitespace-nowrap">
            {badge}
          </span>
        </div>
      )}
      {popular && !badge && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <span className="px-4 py-1.5 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-sm font-bold text-black">
            Most Popular
          </span>
        </div>
      )}

      <div className="text-center mb-8">
        <h3 className="text-xl font-semibold text-white mb-3">{name}</h3>
        <div className="flex items-baseline justify-center gap-1 mb-2">
          <span className="text-5xl font-bold text-white">{displayPrice}</span>
          <span className="text-gray-500">{displayPeriod}</span>
        </div>
        {isYearly && yearlySavings > 0 && (
          <p className="text-emerald-400 text-sm font-medium mb-1">
            Save ${yearlySavings}/year
          </p>
        )}
        <p className="text-gray-400 text-sm">{description}</p>
      </div>

      <ul className="space-y-4 mb-8 flex-grow">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start gap-3">
            <div className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0 mt-0.5">
              <Check className="w-3 h-3 text-emerald-400" />
            </div>
            <span className="text-gray-300">{feature}</span>
          </li>
        ))}
      </ul>

      <div className="mt-auto">
        <button
          onClick={handleClick}
          className={clsx(
            'w-full py-4 rounded-xl font-semibold transition-all duration-300',
            popular
              ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-black hover:shadow-lg hover:shadow-emerald-500/25'
              : 'bg-white/10 text-white hover:bg-white/20'
          )}
        >
          {ctaText}
        </button>
      </div>
    </div>
  );
}

// FAQ Item
function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-white/10 last:border-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-6 flex items-center justify-between text-left group"
      >
        <span className="font-medium text-white group-hover:text-emerald-400 transition-colors">{question}</span>
        <ChevronDown
          className={clsx(
            'w-5 h-5 text-gray-500 transition-transform duration-300',
            isOpen && 'rotate-180'
          )}
        />
      </button>
      <div className={clsx(
        'overflow-hidden transition-all duration-300',
        isOpen ? 'max-h-96 pb-6' : 'max-h-0'
      )}>
        <p className="text-gray-400 leading-relaxed">{answer}</p>
      </div>
    </div>
  );
}

// Dashboard preview section — tabbed showcase of app features
function DashboardPreview() {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'analysis' | 'simulator'>('dashboard');

  const tabs = [
    { id: 'dashboard' as const, label: 'Market Signals', icon: BarChart3 },
    { id: 'analysis' as const, label: 'Stock Analysis', icon: Brain },
    { id: 'simulator' as const, label: 'Backtesting', icon: History },
  ];

  const buyStocks = [
    { rank: 1, ticker: 'INTC', name: 'Intel Corporation', signal: 'STRONG BUY', disconnect: 82 },
    { rank: 2, ticker: 'NKE', name: 'Nike Inc.', signal: 'BUY', disconnect: 74 },
    { rank: 3, ticker: 'PYPL', name: 'PayPal Holdings', signal: 'BUY', disconnect: 68 },
  ];

  const sellStocks = [
    { rank: 1, ticker: 'PLTR', name: 'Palantir Technologies', signal: 'STRONG SELL', disconnect: 79 },
    { rank: 2, ticker: 'SMCI', name: 'Super Micro Computer', signal: 'SELL', disconnect: 72 },
    { rank: 3, ticker: 'MSTR', name: 'MicroStrategy Inc.', signal: 'SELL', disconnect: 66 },
  ];

  const backtestSignals = [
    { date: 'Nov 15, 2024', ticker: 'INTC', name: 'Intel Corp', signal: 'STRONG BUY', entry: 21.30, current: 24.50, returnPct: 15.0 },
    { date: 'Oct 22, 2024', ticker: 'DIS', name: 'Walt Disney', signal: 'BUY', entry: 89.40, current: 112.80, returnPct: 26.2 },
    { date: 'Sep 8, 2024', ticker: 'NKE', name: 'Nike Inc.', signal: 'BUY', entry: 71.20, current: 78.90, returnPct: 10.8 },
  ];

  return (
    <section className="relative py-32 px-6 overflow-hidden">
      <div className="max-w-6xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-12">
          <FeatureBadge className="mb-6">
            <BarChart3 className="w-3 h-3" />
            Platform Preview
          </FeatureBadge>
          <h2 className="text-4xl md:text-5xl font-serif mb-6">
            See What You Get
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Real-time AI-powered signals, deep stock analysis, and historical backtesting — all in one platform.
          </p>
        </div>

        {/* Browser frame */}
        <div className="relative">
          {/* Glow behind frame */}
          <div className="absolute -inset-8 bg-emerald-500/5 rounded-[40px] blur-3xl pointer-events-none" />

          <div className="relative bg-[#0a0a12] rounded-2xl border border-white/10 overflow-hidden shadow-2xl shadow-black/50">
            {/* Browser chrome */}
            <div className="flex items-center px-4 py-3 border-b border-white/5 bg-[#08080d]">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
              </div>
              <div className="flex-1 flex justify-center">
                <div className="px-4 py-1 rounded-md bg-white/5 text-[11px] text-gray-500 font-mono">
                  app.stocksentiment.io
                </div>
              </div>
              <div className="w-[52px]" />
            </div>

            {/* Feature tabs */}
            <div className="flex border-b border-white/5 px-2 sm:px-4 bg-[#09090f] overflow-x-auto scrollbar-none">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={clsx(
                    'flex items-center gap-2 px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium transition-all border-b-2 whitespace-nowrap cursor-pointer',
                    activeTab === tab.id
                      ? 'text-emerald-400 border-emerald-400'
                      : 'text-gray-500 border-transparent hover:text-gray-300'
                  )}
                >
                  <tab.icon className="w-3.5 h-3.5" />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div className="p-4 md:p-6 space-y-4 min-h-[440px]">

              {/* ===== MARKET SIGNALS TAB ===== */}
              {activeTab === 'dashboard' && (
                <>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-white">Market Dashboard</h3>
                      <p className="text-xs text-gray-500">
                        Contrarian opportunities across 47 stocks
                      </p>
                    </div>
                    <div className="hidden sm:flex items-center gap-1.5 text-[11px] text-gray-600">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      Updated 3 min ago
                    </div>
                  </div>

                  {/* Metrics */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="col-span-2 bg-white/[0.03] rounded-xl border border-white/5 p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-gray-500">Avg Market Sentiment</span>
                        <span className="text-lg font-bold font-mono text-amber-400">42</span>
                      </div>
                      <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full w-[42%] rounded-full bg-gradient-to-r from-red-500 to-amber-500" />
                      </div>
                      <div className="flex justify-between mt-1 text-[10px] text-gray-600">
                        <span>0</span><span>50</span><span>100</span>
                      </div>
                    </div>
                    <div className="bg-white/[0.03] rounded-xl border border-white/5 p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                          <TrendingUp className="w-4 h-4 text-emerald-400" />
                        </div>
                        <div>
                          <p className="text-[11px] text-gray-500">Buy Signals</p>
                          <p className="text-xl font-bold text-emerald-400">12</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white/[0.03] rounded-xl border border-white/5 p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-red-500/10 flex items-center justify-center">
                          <TrendingDown className="w-4 h-4 text-red-400" />
                        </div>
                        <div>
                          <p className="text-[11px] text-gray-500">Sell Signals</p>
                          <p className="text-xl font-bold text-red-400">8</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Stock lists */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-7 h-7 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                          <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-white">Buy Opportunities</h4>
                          <p className="text-[10px] text-gray-500">Low sentiment + strong fundamentals</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        {buyStocks.map((stock) => (
                          <div key={stock.ticker} className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/5">
                            <span className="text-[11px] text-gray-600 w-5">#{stock.rank}</span>
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500/15 to-teal-500/10 flex items-center justify-center flex-shrink-0">
                              <span className="text-[10px] font-bold text-emerald-400">{stock.ticker.slice(0, 2)}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-sm font-bold text-white">{stock.ticker}</span>
                                <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-emerald-500/10 text-emerald-400 whitespace-nowrap">{stock.signal}</span>
                              </div>
                              <p className="text-[11px] text-gray-500 truncate">{stock.name}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-base font-bold font-mono text-emerald-400">{stock.disconnect}</p>
                              <p className="text-[9px] text-gray-600">disconnect</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-7 h-7 rounded-lg bg-red-500/10 flex items-center justify-center">
                          <TrendingDown className="w-3.5 h-3.5 text-red-400" />
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-white">Sell Signals</h4>
                          <p className="text-[10px] text-gray-500">High sentiment = stretched</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        {sellStocks.map((stock) => (
                          <div key={stock.ticker} className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/5">
                            <span className="text-[11px] text-gray-600 w-5">#{stock.rank}</span>
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-red-500/15 to-orange-500/10 flex items-center justify-center flex-shrink-0">
                              <span className="text-[10px] font-bold text-red-400">{stock.ticker.slice(0, 2)}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-sm font-bold text-white">{stock.ticker}</span>
                                <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-red-500/10 text-red-400 whitespace-nowrap">{stock.signal}</span>
                              </div>
                              <p className="text-[11px] text-gray-500 truncate">{stock.name}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-base font-bold font-mono text-red-400">{stock.disconnect}</p>
                              <p className="text-[9px] text-gray-600">disconnect</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* ===== STOCK ANALYSIS TAB ===== */}
              {activeTab === 'analysis' && (
                <>
                  {/* Stock header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/20 to-teal-500/10 flex items-center justify-center">
                        <span className="text-sm font-bold text-emerald-400">IN</span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-bold text-white">INTC</h3>
                          <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/10 text-emerald-400">
                            STRONG BUY
                          </span>
                        </div>
                        <p className="text-xs text-gray-500">Intel Corporation</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-white font-mono">$24.50</p>
                      <p className="text-xs text-red-400">-1.2% today</p>
                    </div>
                  </div>

                  {/* Score cards */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-white/[0.03] rounded-xl border border-white/5 p-4 text-center">
                      <p className="text-[11px] text-gray-500 mb-1">Disconnect</p>
                      <p className="text-2xl font-bold font-mono text-emerald-400">82</p>
                      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden mt-2">
                        <div className="h-full w-[82%] rounded-full bg-emerald-500" />
                      </div>
                    </div>
                    <div className="bg-white/[0.03] rounded-xl border border-white/5 p-4 text-center">
                      <p className="text-[11px] text-gray-500 mb-1">Sentiment</p>
                      <p className="text-2xl font-bold font-mono text-red-400">24</p>
                      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden mt-2">
                        <div className="h-full w-[24%] rounded-full bg-red-500" />
                      </div>
                    </div>
                    <div className="bg-white/[0.03] rounded-xl border border-white/5 p-4 text-center">
                      <p className="text-[11px] text-gray-500 mb-1">Fundamental</p>
                      <p className="text-2xl font-bold font-mono text-emerald-400">72</p>
                      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden mt-2">
                        <div className="h-full w-[72%] rounded-full bg-emerald-500" />
                      </div>
                    </div>
                  </div>

                  {/* AI Summary */}
                  <div className="bg-white/[0.03] rounded-xl border border-white/5 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Brain className="w-4 h-4 text-emerald-400" />
                      <span className="text-sm font-semibold text-white">AI Sentiment Summary</span>
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-emerald-500/10 text-emerald-400">AI</span>
                    </div>
                    <p className="text-sm text-gray-400 leading-relaxed">
                      Intel is experiencing significant negative sentiment driven by concerns about foundry
                      execution and market share loss to AMD. However, fundamentals tell a different story —
                      strong cash reserves of $29B, improving manufacturing yields, and $8.5B in CHIPS Act
                      funding provide substantial downside protection. The disconnect between fear-driven
                      sentiment and solid balance sheet metrics creates a compelling contrarian opportunity.
                    </p>
                  </div>

                  {/* Key themes */}
                  <div>
                    <p className="text-xs text-gray-500 mb-2">Key Themes</p>
                    <div className="flex flex-wrap gap-2">
                      {['Foundry Concerns', 'AMD Competition', 'CHIPS Act', 'AI Infrastructure', 'Cost Cutting'].map((theme) => (
                        <span
                          key={theme}
                          className="px-2.5 py-1 rounded-full text-[11px] bg-white/5 text-gray-400 border border-white/5"
                        >
                          {theme}
                        </span>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* ===== BACKTESTING TAB ===== */}
              {activeTab === 'simulator' && (
                <>
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-white">Backtesting Simulator</h3>
                      <p className="text-xs text-gray-500">Historical signal performance</p>
                    </div>
                    <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold bg-purple-500/10 text-purple-400 border border-purple-500/20">
                      PRO
                    </span>
                  </div>

                  {/* Summary stats */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-white/[0.03] rounded-xl border border-white/5 p-4 text-center">
                      <p className="text-[11px] text-gray-500 mb-1">Win Rate</p>
                      <p className="text-2xl font-bold font-mono text-emerald-400">89%</p>
                    </div>
                    <div className="bg-white/[0.03] rounded-xl border border-white/5 p-4 text-center">
                      <p className="text-[11px] text-gray-500 mb-1">Avg Return</p>
                      <p className="text-2xl font-bold font-mono text-emerald-400">+18.4%</p>
                    </div>
                    <div className="bg-white/[0.03] rounded-xl border border-white/5 p-4 text-center">
                      <p className="text-[11px] text-gray-500 mb-1">Signals Tested</p>
                      <p className="text-2xl font-bold font-mono text-white">156</p>
                    </div>
                  </div>

                  {/* Signals table */}
                  <div className="bg-white/[0.03] rounded-xl border border-white/5 overflow-hidden">
                    <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
                      <History className="w-4 h-4 text-purple-400" />
                      <span className="text-sm font-semibold text-white">Recent Signals</span>
                      <span className="text-xs text-gray-600 ml-auto">3 of 156</span>
                    </div>
                    <div className="divide-y divide-white/5">
                      {backtestSignals.map((signal) => (
                        <div key={signal.ticker} className="p-4 flex items-center gap-3 sm:gap-4">
                          <div className="hidden sm:block text-[11px] text-gray-600 w-24 flex-shrink-0">
                            {signal.date}
                          </div>
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500/15 to-teal-500/10 flex items-center justify-center flex-shrink-0">
                            <span className="text-[10px] font-bold text-emerald-400">{signal.ticker.slice(0, 2)}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm font-bold text-white">{signal.ticker}</span>
                              <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-emerald-500/10 text-emerald-400">
                                {signal.signal}
                              </span>
                            </div>
                            <p className="text-[11px] text-gray-500">{signal.name}</p>
                          </div>
                          <div className="hidden sm:flex items-center gap-1.5 text-xs text-gray-500 flex-shrink-0">
                            <span className="font-mono">${signal.entry.toFixed(2)}</span>
                            <ArrowRight className="w-3 h-3 text-gray-600" />
                            <span className="font-mono text-white">${signal.current.toFixed(2)}</span>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-base font-bold font-mono text-emerald-400">
                              +{signal.returnPct}%
                            </p>
                            <p className="text-[9px] text-gray-600 sm:hidden">
                              ${signal.entry.toFixed(0)} → ${signal.current.toFixed(0)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Gradient fade */}
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#060606] via-[#060606]/80 to-transparent rounded-b-2xl pointer-events-none" />
        </div>
      </div>
    </section>
  );
}

// Main Landing component
export function Landing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#060606] text-white overflow-hidden">
      {/* Background */}
      <GridBackground />

      {/* Navigation */}
      <nav className="relative z-50 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.svg" alt="SentimentEdge" className="w-8 h-8" />
              <span className="text-xl font-bold">
                <span className="text-white">Sentiment</span>
                <span className="text-emerald-400">Edge</span>
              </span>
            </Link>

            {/* Nav Links */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-gray-400 hover:text-white transition-colors text-sm">
                Features
              </a>
              <a href="#how-it-works" className="text-gray-400 hover:text-white transition-colors text-sm">
                How It Works
              </a>
              <a href="#pricing" className="text-gray-400 hover:text-white transition-colors text-sm">
                Pricing
              </a>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-4">
              {/* Social icons */}
              <div className="hidden md:flex items-center gap-2">
                <SocialIcon href="https://twitter.com/sentimentedge">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </SocialIcon>
                <SocialIcon href="https://discord.gg/sentimentedge">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                  </svg>
                </SocialIcon>
              </div>

              <button
                onClick={() => navigate('/login')}
                className="text-gray-400 hover:text-white transition-colors text-sm"
              >
                Sign In
              </button>
              <button
                onClick={() => navigate('/login')}
                className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-black font-semibold text-sm hover:shadow-lg hover:shadow-emerald-500/25 transition-all"
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-24 pb-32 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            {/* Main headline with serif font */}
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-serif font-normal leading-[1.1] mb-8 tracking-tight">
              All-in-One Stock
              <br />
              <RotatingText /> Analysis
            </h1>

            {/* Subtitle with rotating keywords */}
            <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-gray-400 mb-12 px-4 text-sm md:text-base">
              <span>AI Sentiment</span>
              <span className="text-emerald-400">•</span>
              <span className="text-emerald-400">Disconnect Detection</span>
              <span className="text-emerald-400 hidden sm:inline">•</span>
              <span className="hidden sm:inline">Fundamentals</span>
              <span className="hidden sm:inline text-gray-600">&</span>
              <span className="hidden sm:inline">More</span>
            </div>
          </div>

          {/* Input demo card */}
          <div className="max-w-2xl mx-auto mb-16">
            <div className="relative bg-[#0c0c0c] border border-white/10 rounded-2xl p-4">
              <p className="text-gray-400 text-sm mb-4">
                Find contrarian opportunities with limited downside: BUY when sentiment is LOW but fundamentals are STRONG.
                Our AI identifies when fear creates buying opportunities and when euphoria signals it's time to sell.
              </p>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-gray-500">
                  <Sparkles className="w-4 h-4 shrink-0" />
                  <span className="text-xs sm:text-sm">AI-powered sentiment from X & Reddit</span>
                </div>
                <a
                  href={getAppUrl()}
                  className="px-4 py-2 sm:px-6 sm:py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-black font-semibold text-sm hover:shadow-lg hover:shadow-emerald-500/25 transition-all whitespace-nowrap text-center"
                >
                  Try Demo
                </a>
              </div>
            </div>
          </div>

          {/* Demo preview section */}
          <div className="relative">
            {/* Floating cards */}
            <div className="flex flex-col lg:flex-row items-center justify-center gap-6">
              <DemoPreviewCard />

              {/* Side stats cards */}
              <div className="hidden lg:flex flex-col gap-4">
                <FloatingCard className="p-4" delay={200}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                      <LineChart className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">89%</p>
                      <p className="text-xs text-gray-500">Backtest Win Rate</p>
                    </div>
                  </div>
                </FloatingCard>

                <FloatingCard className="p-4" delay={400}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-teal-500/20 flex items-center justify-center">
                      <Globe className="w-5 h-5 text-teal-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">1.2M+</p>
                      <p className="text-xs text-gray-500">Posts Analyzed Daily</p>
                    </div>
                  </div>
                </FloatingCard>

                <FloatingCard className="p-4" delay={600}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                      <Target className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">47 days</p>
                      <p className="text-xs text-gray-500">Avg. Recovery Time</p>
                    </div>
                  </div>
                </FloatingCard>
              </div>
            </div>
          </div>

          {/* Tagline */}
          <div className="text-center mt-20">
            <p className="text-lg text-gray-500 italic">
              "Be fearful when others are greedy, and greedy when others are fearful."
            </p>
            <div className="flex items-center justify-center gap-2 mt-2">
              <ChevronDown className="w-4 h-4 text-gray-600 animate-bounce" />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="relative py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <FeatureBadge className="mb-6">
              <Zap className="w-3 h-3" />
              Features
            </FeatureBadge>
            <h2 className="text-4xl md:text-5xl font-serif mb-6">
              Your Sentiment Research Toolkit
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto text-lg">
              Everything you need to find market disconnects and make informed investment decisions.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureShowcase
              icon={Brain}
              title="AI Sentiment Analysis"
              description="Our AI reads millions of posts from X, Reddit, and news sites to generate human-readable sentiment summaries."
              badge="AI"
            />
            <FeatureShowcase
              icon={TrendingDown}
              title="Disconnect Detection"
              description="Find contrarian opportunities: BUY when sentiment is LOW but fundamentals are STRONG. SELL when sentiment is HIGH (stretched, priced for perfection)."
            />
            <FeatureShowcase
              icon={BarChart3}
              title="Fundamental Scoring"
              description="Compare sentiment against real fundamentals: revenue growth, margins, cash flow, and balance sheet."
            />
            <FeatureShowcase
              icon={History}
              title="Historical Analysis"
              description="See how past sentiment disconnects resolved. Backtest strategies against years of data."
              badge="Pro"
            />
            <FeatureShowcase
              icon={Search}
              title="Stock Screener"
              description="Filter by sentiment score, fundamental score, sector, market cap, and more."
            />
            <FeatureShowcase
              icon={MessageSquare}
              title="Real-time Alerts"
              description="Get notified when new disconnect signals appear. Never miss an opportunity."
              badge="Pro"
            />
          </div>
        </div>
      </section>

      {/* Dashboard Preview */}
      <DashboardPreview />

      {/* How It Works */}
      <section id="how-it-works" className="relative py-32 px-6 bg-gradient-to-b from-transparent via-white/[0.02] to-transparent">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-20">
            <FeatureBadge className="mb-6">
              <Activity className="w-3 h-3" />
              How It Works
            </FeatureBadge>
            <h2 className="text-4xl md:text-5xl font-serif mb-6">
              Three Simple Steps
            </h2>
          </div>

          <div className="space-y-16">
            {[
              {
                step: '01',
                title: 'We Analyze Sentiment',
                description: 'Our AI scans X, Reddit, and news sites 24/7. We identify when fear or euphoria is driving prices away from fair value.',
              },
              {
                step: '02',
                title: 'We Find the Gap',
                description: 'LOW sentiment + STRONG fundamentals = BUY opportunity (market is too pessimistic). HIGH sentiment = SELL signal (stock is stretched, priced for perfection with only downside risk).',
              },
              {
                step: '03',
                title: 'You Do the Research',
                description: 'We surface contrarian opportunities with limited downside and asymmetric upside. You make your own informed decisions.',
              },
            ].map((item, index) => (
              <div key={index} className="flex gap-8 items-start">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-teal-500/10 flex items-center justify-center shrink-0">
                  <span className="text-2xl font-bold text-emerald-400">{item.step}</span>
                </div>
                <div>
                  <h3 className="text-2xl font-semibold text-white mb-3">{item.title}</h3>
                  <p className="text-gray-400 text-lg leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <PricingSection />


      {/* FAQ */}
      <section className="relative py-32 px-6 bg-gradient-to-b from-transparent via-white/[0.02] to-transparent">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-serif mb-6">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="bg-[#0a0a0a] rounded-3xl border border-white/10 p-8">
            <FAQItem
              question="What is a 'disconnect score'?"
              answer="The disconnect score measures the GAP between fundamentals and sentiment. When fundamentals are STRONG but sentiment is LOW (fear/pessimism), you get a BUY signal — the market is wrong. When sentiment is HIGH (euphoria), you get a SELL signal — the stock is stretched with only downside risk."
            />
            <FAQItem
              question="How does the AI sentiment analysis work?"
              answer="We use AI to analyze millions of posts from X, Reddit, and news articles. Unlike simple sentiment scores, our AI generates human-readable summaries that explain what people are actually saying."
            />
            <FAQItem
              question="Is this financial advice?"
              answer="No. SentimentEdge is a research tool that shows you sentiment data and analysis. We don't recommend specific trades. All investment decisions should be your own."
            />
            <FAQItem
              question="How far back does historical data go?"
              answer="Our historical sentiment data goes back 3+ years, allowing you to see how past sentiment disconnects resolved and backtest strategies."
            />
            <FAQItem
              question="Can I cancel anytime?"
              answer="Yes. You can cancel your subscription at any time with no questions asked. Your access continues until the end of your billing period."
            />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-32 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="relative p-16 rounded-[32px] overflow-hidden">
            {/* Background gradient */}
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 via-teal-500/20 to-emerald-500/20 rounded-[32px]" />
            <div className="absolute inset-0 bg-[#060606]/80 backdrop-blur-xl rounded-[32px]" />
            <div className="absolute inset-[1px] rounded-[31px] border border-emerald-500/20" />

            <div className="relative">
              <h2 className="text-4xl md:text-5xl font-serif mb-6">
                Ready to Find Your Edge?
              </h2>
              <p className="text-gray-400 mb-10 max-w-xl mx-auto text-lg">
                Join thousands of researchers using AI-powered sentiment analysis to find opportunities others miss.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="inline-flex items-center gap-2 px-5 py-3 md:px-8 md:py-4 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-black font-semibold text-base md:text-lg hover:shadow-lg hover:shadow-emerald-500/25 transition-all"
              >
                Start Your Free Trial
                <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
              </button>
              <p className="text-gray-600 text-sm mt-6">No credit card required</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative border-t border-white/5 py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-5 gap-12 mb-12">
            {/* Brand */}
            <div className="md:col-span-2">
              <Link to="/" className="flex items-center gap-2 mb-4">
                <img src="/logo.svg" alt="SentimentEdge" className="w-8 h-8" />
                <span className="text-xl font-bold">
                  <span className="text-white">Sentiment</span>
                  <span className="text-emerald-400">Edge</span>
                </span>
              </Link>
              <p className="text-gray-500 text-sm max-w-xs">
                AI-powered sentiment research for smarter investing decisions.
              </p>
              <div className="flex items-center gap-3 mt-6">
                <SocialIcon href="https://twitter.com/sentimentedge">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </SocialIcon>
                <SocialIcon href="https://discord.gg/sentimentedge">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                  </svg>
                </SocialIcon>
              </div>
            </div>

            {/* Links */}
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-3 text-gray-500 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href={getAppUrl()} className="hover:text-white transition-colors">Dashboard</a></li>
                <li><a href={getAppUrl('/api-docs')} className="hover:text-white transition-colors">API Docs</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-3 text-gray-500 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">About</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-3 text-gray-500 text-sm">
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
                <li><Link to="/disclaimer" className="hover:text-white transition-colors">Disclaimer</Link></li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-600 text-sm">
              © 2026 SentimentEdge. All rights reserved.
            </p>
            <p className="text-gray-700 text-xs">
              Investing involves risk. Past performance is not indicative of future results.
            </p>
          </div>
        </div>
      </footer>

      {/* Custom styles for animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 4s ease-in-out infinite;
        }
        .font-serif {
          font-family: ui-serif, Georgia, Cambria, "Times New Roman", Times, serif;
        }
      `}</style>
    </div>
  );
}
